import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { BackendResponse } from './types/common';

type RequestHeaders = {
  delete?: (headerName: string) => void;
  [key: string]: unknown;
};

const redirectToLogin = () => {
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('currentPeriodId');
  window.dispatchEvent(new Event('loginStatusChanged'));

  if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/login') {
    window.location.href = '/login';
  }
};

// 创建 axios 实例
const instance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    // 添加 JWT token
    const token = sessionStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const isFormData = config.data instanceof FormData;

    if (isFormData) {
      // 让浏览器自动设置 multipart/form-data 边界
      if (config.headers) {
        const requestHeaders = config.headers as RequestHeaders;
        if (typeof requestHeaders.delete === 'function') {
          requestHeaders.delete('Content-Type');
          requestHeaders.delete('content-type');
        } else {
          delete requestHeaders['Content-Type'];
          delete requestHeaders['content-type'];
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Blob 响应直接返回，不做转换
    if (response.config.responseType === 'blob') {
      return response;
    }

    const responseData = response.data as BackendResponse;

    // 处理业务错误（statusCode.value < 400 为成功）
    if (responseData.statusCode && responseData.statusCode.value >= 400) {
      if (responseData.statusCode.value === 401 || responseData.statusCode.value === 499) {
        redirectToLogin();
      }

      // 优先使用 messages 数组（用分号拼接），其次使用 error.message，最后使用默认消息
      let errorMessage = '请求失败';
      if (responseData.messages && responseData.messages.length > 0) {
        errorMessage = responseData.messages.join('；');
      } else if (responseData.error?.message) {
        errorMessage = responseData.error.message;
      }
      return Promise.reject(new Error(errorMessage));
    }

    return response;
  },
  (error) => {
    // 处理 HTTP 错误
    if (error.response) {
      switch (error.response.status) {
        case 401:
          redirectToLogin();
          break;
        case 403:
          console.error('无权限访问');
          break;
        case 404:
          console.error('请求的资源不存在');
          break;
        case 500:
          console.error('服务器错误');
          break;
        default:
          console.error('请求失败:', error.message);
      }
    } else {
      console.error('网络错误:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * 请求方法封装
 * ⚠️ 直接返回 BackendResponse<T>，而不是 AxiosResponse
 */
export const request = {
  get: async <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<BackendResponse<T>> => {
    const response = await instance.get(url, config);
    return response.data;
  },

  post: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<BackendResponse<T>> => {
    const response = await instance.post(url, data, config);
    return response.data;
  },

  put: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<BackendResponse<T>> => {
    const response = await instance.put(url, data, config);
    return response.data;
  },

  delete: async <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<BackendResponse<T>> => {
    const response = await instance.delete(url, config);
    return response.data;
  },

  patch: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<BackendResponse<T>> => {
    const response = await instance.patch(url, data, config);
    return response.data;
  },
};

export default instance;
